import { Column_Sort_Params } from "../interfaces/column_sort_params";
import { _Object, CommonPrefix } from "@aws-sdk/client-s3";

/**
 * Return the opposing sort order enum value. If an invalid sort order input is provided, default to "D"
 * @param order The current sorting order, either "A" or "D"
 */
const toggleSortOrder = (order: Column_Sort_Params) => {
    return order === Column_Sort_Params.D ? Column_Sort_Params.A : Column_Sort_Params.D
}

/**
 * Sort columns by name, case-insensitive, ascending for fields/objects.
 * @param a
 * @param b
 */
const sortByNameAscFunc = (a: _Object, b: _Object) => a.Key?.toLowerCase() as string > (b.Key?.toLowerCase() as string) ? 1 : -1

/**
 * Sort columns by name, case-insensitive, ascending for directories.
 * @param a
 * @param b
 */
const sortDirByNameAscFunc = (a: CommonPrefix, b: CommonPrefix) => a.Prefix?.toLowerCase() as string > (b.Prefix?.toLowerCase() as string) ? 1 : -1


/**
 * Sort columns by name, case-insensitive, descending for fields/objects.
 * @param a
 * @param b
 */
const sortByNameDescFunc = (a: _Object, b: _Object) => b.Key?.toLowerCase() as string > (a.Key?.toLowerCase() as string) ? 1 : -1

/**
 * Sort columns by name, case-insensitive, descending for directories.
 * @param a
 * @param b
 */
const sortDirByNameDescFunc = (a: CommonPrefix, b: CommonPrefix) => b.Prefix?.toLowerCase() as string > (a.Prefix?.toLowerCase() as string) ? 1 : -1


/**
 * Sort columns by modified date, ascending.
 */
const sortByDateAscFunc = (a: _Object, b: _Object) => Number(a.LastModified) - Number(b.LastModified)

/**
 * Sort columns by modified date, descending.
 * @param a
 * @param b
 */
const sortByDateDescFunc = (a: _Object, b: _Object) => Number(b.LastModified) - Number(a.LastModified)

/**
 * Sort columns by file size, ascending.
 * @param a
 * @param b
 */
const sortBySizeAscFunc = (a: _Object, b: _Object) => (a.Size as number) - (b.Size as number)

/**
 * Sort columns by file size, descending.
 * @param a
 * @param b
 */
const sortBySizeDescFunc = (a: _Object, b: _Object) => (b.Size as number) - (a.Size as number)

/**
 * Build the search parameter string to specify which column to sort and by how they will be sorted.
 * @param columnSortField The column to sort. One of [ N | M | S].
 * @param columnSortBy The current order to sort the column by. One of [ A | D ]. The output of this string will toggle the value to the other option.
 */
const sortTermBuilder = (columnSortField: Column_Sort_Params, columnSortBy: Column_Sort_Params) => `${Column_Sort_Params.C}=${columnSortField}&${Column_Sort_Params.O}=${toggleSortOrder(columnSortBy)}`

/**
 * Return a sorted array, based on the column to sort by and the order.
 * @param array An unsorted array of objects.
 * @param columnSortField The column to sort. One of [ N | M | S].
 * @param columnSortBy The order to sort the column by. One of [ A | D ].
 */
const sortedDataBuilder = (array: _Object[] | undefined, columnSortField: Column_Sort_Params, columnSortBy: Column_Sort_Params) => {
    if (array === undefined) return []

    switch (columnSortField) {
        case Column_Sort_Params.N:
            return columnSortBy === Column_Sort_Params.A ? array.sort(sortByNameAscFunc) : array.sort(sortByNameDescFunc)
        case Column_Sort_Params.M:
            return columnSortBy === Column_Sort_Params.A ? array.sort(sortByDateAscFunc) : array.sort(sortByDateDescFunc)
        case Column_Sort_Params.S:
            return columnSortBy === Column_Sort_Params.A ? array.sort(sortBySizeAscFunc) : array.sort(sortBySizeDescFunc)
        default:
            return array.sort(sortByNameAscFunc)
    }
}
/**
 * Return a sorted array, based on the column to sort by and the order.
 * @param array An unsorted array of CommonPrefix/directories.
 * @param columnSortField The column to sort. One of [ N | M | S].
 * @param columnSortBy The order to sort the column by. One of [ A | D ].
 */
const sortedDirectoryBuilder = (array: CommonPrefix[] | undefined, columnSortField: Column_Sort_Params, columnSortBy: Column_Sort_Params) => {
    if (array === undefined) return []

    switch (columnSortField) {
        case Column_Sort_Params.N:
            return columnSortBy === Column_Sort_Params.A ? array.sort(sortDirByNameAscFunc) : array.sort(sortDirByNameDescFunc)
        default:
            return array.sort(sortDirByNameAscFunc)
    }
}
    

export {
    toggleSortOrder,
    sortByNameAscFunc,
    sortByNameDescFunc,
    sortByDateAscFunc,
    sortByDateDescFunc,
    sortBySizeAscFunc,
    sortBySizeDescFunc,
    sortTermBuilder,
    sortedDataBuilder,
    sortedDirectoryBuilder
}